<script setup lang="ts">
import { useSafeBack } from '@/composables/useSafeBack'

defineOptions({ name: 'PageHeader' })

const props = withDefaults(defineProps<{
  title?: string
  /** Show a back button; the value is the fallback path for direct loads. */
  back?: string
  backLabel?: string
  /** Big title row under the bar (iOS large-title style). */
  large?: boolean
  /** No bar background or border, for landing and auth screens. */
  transparent?: boolean
}>(), { backLabel: 'Back' })

const safeBack = useSafeBack(() => props.back ?? '/')
</script>

<template>
  <header class="page-header" :class="{ 'page-header-transparent': transparent }">
    <div class="page-header-bar">
      <div class="page-header-left">
        <button
          v-if="back !== undefined"
          type="button"
          class="page-header-back"
          :aria-label="backLabel"
          @click="safeBack()"
        >
          <span aria-hidden="true">‹</span>
        </button>
        <slot name="left" />
      </div>
      <div v-if="title && !large" class="page-header-title">{{ title }}</div>
      <div class="page-header-right">
        <slot name="right" />
      </div>
    </div>
    <div v-if="title && large" class="page-header-large-title">{{ title }}</div>
  </header>
</template>

<style scoped>
.page-header {
  position: relative;
  z-index: 30;
  flex: none;
  padding: env(safe-area-inset-top) env(safe-area-inset-right) 0 env(safe-area-inset-left);
  border-bottom: 1px solid var(--app-bars-border-color);
  background: var(--app-bars-bg-color);
  color: var(--app-bars-text-color);
}
.page-header-transparent {
  border-bottom-color: transparent;
  background: transparent;
}
.page-header-bar {
  position: relative;
  display: flex;
  height: 44px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 8px;
}
.page-header-left,
.page-header-right {
  position: relative;
  z-index: 1;
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 4px;
}
/* Centered over the whole bar, like iOS; side slots sit above it. */
.page-header-title {
  position: absolute;
  left: 50%;
  max-width: calc(100% - 176px);
  transform: translateX(-50%);
  overflow: hidden;
  font-size: 17px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.page-header-back {
  display: flex;
  height: 44px;
  align-items: center;
  padding: 0 8px;
  color: var(--app-bars-text-muted);
  font-size: 1.5rem;
  line-height: 1;
}
.page-header-back:hover {
  color: var(--ink);
}
.page-header-large-title {
  padding: 0 16px 8px;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
}
</style>
